import React, { useState, useRef, useEffect, useCallback } from 'react';
import { adminFetchInit } from '../utils/adminToken';

const ADD_PRODUCT_DEFAULT_PRICE = '10.00';
const ADD_PRODUCT_DEFAULT_STOCK = '5';

const AddProductModal = ({ isOpen, onClose, onProductAdded, enableVendorEdit = false }) => {
  const modalRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [images, setImages] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: ADD_PRODUCT_DEFAULT_PRICE,
    comparePrice: '',
    costPerItem: '',
    category: '',
    vendor: '',
    tags: '',
    stock: ADD_PRODUCT_DEFAULT_STOCK,
    weight: '0.0',
    weightUnit: 'kg',
    status: 'active',
    trackQuantity: true,
    continueSellingOutOfStock: false,
    hasSKU: false,
    sku: '',
    barcode: '',
    isPhysicalProduct: true,
    chargeTax: true
  });

  // Add state for validation errors
  const [validationErrors, setValidationErrors] = useState({
    price: '',
    stock: ''
  });

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear validation errors when user types
    if (name === 'price' || name === 'stock') {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle image upload (placeholder - would integrate with actual image upload service)
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    // In a real implementation, you would upload these to a service like Cloudinary
    // For now, we'll just create object URLs for preview
    const newImages = files.slice(0, 5 - images.length).map(file => ({
      url: URL.createObjectURL(file),
      alt: formData.title,
      file: file
    }));
    setImages(prev => [...prev, ...newImages]);
  };

  // Remove image
  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      title: '',
      description: '',
      price: ADD_PRODUCT_DEFAULT_PRICE,
      comparePrice: '',
      costPerItem: '',
      category: '',
      vendor: '',
      tags: '',
      stock: ADD_PRODUCT_DEFAULT_STOCK,
      weight: '0.0',
      weightUnit: 'kg',
      status: 'active',
      trackQuantity: true,
      continueSellingOutOfStock: false,
      hasSKU: false,
      sku: '',
      barcode: '',
      isPhysicalProduct: true,
      chargeTax: true
    });
    setImages([]);
    setSubmitError(null);
    setValidationErrors({
      price: '',
      stock: ''
    });
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    // Validation logic
    const errors = {};
    
    // Check price validation
    const price = parseFloat(formData.price);
    if (!formData.price || price <= 0) {
      errors.price = 'Price must be greater than 0';
    }
    
    // Check quantity validation (only if tracking quantity)
    if (formData.trackQuantity) {
      const stock = parseInt(formData.stock);
      if (!formData.stock || stock <= 0) {
        errors.stock = 'Quantity must be greater than 0';
      }
    }
    
    // If there are validation errors, set them and stop submission
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Clear any existing errors
    setValidationErrors({
      price: '',
      stock: ''
    });

    setIsSubmitting(true);

    try {
      console.log('📦 Submitting product creation...');
      
      let uploadedImages = [];
      
      // First, upload images to Cloudinary if there are any
      const blobImages = images.filter(img => img.url.startsWith('blob:'));
      if (blobImages.length > 0) {
        console.log('🖼️ Uploading images to Cloudinary first...');
        
        try {
          // Create FormData to upload images
          const formData_upload = new FormData();
          
          // Add product title for alt text
          formData_upload.append('title', formData.title);
          
          // Add each blob image as a file
          for (let i = 0; i < blobImages.length; i++) {
            const imageData = blobImages[i];
            if (imageData.file) {
              formData_upload.append(`image_${i}`, imageData.file);
            }
          }
          
          // Upload to Cloudinary
          const uploadResponse = await fetch('/.netlify/functions/product-image-upload', adminFetchInit({
            method: 'POST',
            body: formData_upload
          }));
          
          const uploadResult = await uploadResponse.json();
          
          if (uploadResult.success) {
            uploadedImages = uploadResult.images;
            console.log('✅ Images uploaded successfully:', uploadedImages);
          } else {
            console.warn('⚠️ Image upload failed:', uploadResult.error);
            // Continue without images
          }
        } catch (uploadError) {
          console.warn('⚠️ Image upload error:', uploadError);
          // Continue without images - don't fail the entire product creation
        }
      }
      
      // Prepare product data matching Shopify REST API format
      const productData = {
        product: {
          title: formData.title,
          body_html: formData.description ? `<p>${formData.description}</p>` : '',
          vendor: formData.vendor,
          product_type: formData.category,
          status: formData.status,
          tags: formData.tags,
          variants: [
            {
              price: formData.price,
              inventory_quantity: parseInt(formData.stock),
              weight: parseFloat(formData.weight),
              weight_unit: formData.weightUnit,
              inventory_management: formData.trackQuantity ? 'shopify' : null,
              inventory_policy: formData.continueSellingOutOfStock ? 'continue' : 'deny',
              compare_at_price: formData.comparePrice || null,
              cost: formData.costPerItem || null,
              sku: formData.hasSKU && formData.sku ? formData.sku : null,
              barcode: formData.barcode || null,
              taxable: formData.chargeTax,
              requires_shipping: formData.isPhysicalProduct
            }
          ]
        }
      };

      // Add uploaded images to product data
      if (uploadedImages.length > 0) {
        productData.product.images = uploadedImages.map(img => ({
          src: img.src,
          alt: img.alt || formData.title
        }));
      }

      console.log('📋 Product data:', productData);

      const response = await fetch('/.netlify/functions/shopify-create-product', adminFetchInit({
        method: 'POST',
        body: JSON.stringify(productData)
      }));

      const result = await response.json();

      if (result.success) {
        console.log('✅ Product created successfully:', result.product);
        
        // Reset form and close modal
        resetForm();
        onClose();
        
        // Notify parent component
        if (onProductAdded) {
          onProductAdded(result.product);
        }
      } else {
        throw new Error(result.error || 'Failed to create product');
      }
    } catch (error) {
      console.error('❌ Error creating product:', error);
      setSubmitError(error.message || 'Failed to create product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  }, [isSubmitting, resetForm, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isOpen && !isSubmitting) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, isSubmitting, handleClose]);

  // Handle click outside modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && isOpen && !isSubmitting) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isSubmitting, handleClose]);

  // Fetch vendor options when modal opens (only if vendor editing is enabled)
  useEffect(() => {
    const fetchVendorOptions = async () => {
      if (!isOpen || !enableVendorEdit) return;

      setLoadingVendors(true);
      try {
        const response = await fetch('/.netlify/functions/get-websites');
        const result = await response.json();
        
        if (result.success) {
          setVendorOptions(result.websites || []);
        } else {
          console.error('Failed to fetch vendor options:', result.error);
          setVendorOptions([]);
        }
      } catch (error) {
        console.error('Error fetching vendor options:', error);
        setVendorOptions([]);
      } finally {
        setLoadingVendors(false);
      }
    };

    fetchVendorOptions();
  }, [isOpen, enableVendorEdit]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div 
        ref={modalRef} 
        className="bg-gray-800 rounded-2xl shadow-2xl max-w-6xl w-full mx-4 max-h-[95vh] overflow-hidden flex flex-col"
      >
        {/* Professional Modal Header */}
        <div className="relative bg-gradient-to-r from-pink-600 to-purple-600 px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Add New Product</h2>
                <p className="text-pink-100 text-sm">Create a new product for your store</p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              disabled={isSubmitting}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-all duration-200 backdrop-blur-sm disabled:opacity-50"
              aria-label="Close dialog"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
        </div>

        {/* Modal Content - Scrollable */}
        <div className="flex-1 overflow-y-auto sidebar-scroll">
          <form onSubmit={handleSubmit} className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Main Product Info */}
              <div className="lg:col-span-2 space-y-8">
                {/* Basic Information Card */}
                <div className="bg-gray-700 border border-gray-600 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-pink-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-white">Basic Information</h3>
                  </div>
                  
                  <div className="space-y-6">
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-3">
                        Product Title <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-pink-500/20 focus:border-pink-500 text-white bg-gray-800 transition-all duration-200 text-lg font-medium"
                        placeholder="e.g., Premium Cotton T-Shirt"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-3">
                        Product Description
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={5}
                        className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-pink-500/20 focus:border-pink-500 text-white bg-gray-800 transition-all duration-200 resize-none"
                        placeholder="Describe your product features, materials, and benefits..."
                      />
                    </div>
                  </div>
                </div>

                {/* Media Upload Card */}
                <div className="bg-gray-700 border border-gray-600 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white">Product Media</h3>
                      <p className="text-sm text-gray-400">Upload high-quality images (up to 5)</p>
                    </div>
                  </div>
                  
                  <div className="border-2 border-dashed border-gray-600 rounded-2xl p-8 hover:border-pink-500 transition-all duration-200">
                    {images.length === 0 ? (
                      <div className="text-center">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          id="image-upload"
                          disabled={isSubmitting}
                        />
                        <label
                          htmlFor="image-upload"
                          className="cursor-pointer group"
                        >
                          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-pink-600/20 to-purple-600/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                            <svg className="w-8 h-8 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          </div>
                          <div className="space-y-2">
                            <p className="text-lg font-semibold text-gray-300">Upload Product Images</p>
                            <p className="text-sm text-gray-400">Drag and drop or click to browse</p>
                            <p className="text-xs text-gray-500">JPG, PNG, WEBP up to 10MB each</p>
                          </div>
                        </label>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {images.map((image, index) => (
                          <div key={index} className="relative group">
                            <div className="aspect-square rounded-xl overflow-hidden border-2 border-gray-600">
                              <img
                                src={image.url}
                                alt={image.alt}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-sm transition-all duration-200 shadow-lg hover:scale-110"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                        {images.length < 5 && (
                          <div className="aspect-square border-2 border-dashed border-gray-600 rounded-xl flex items-center justify-center hover:border-pink-500 transition-colors duration-200">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleImageUpload}
                              className="hidden"
                              id="add-more-images"
                              disabled={isSubmitting}
                            />
                            <label
                              htmlFor="add-more-images"
                              className="cursor-pointer p-4 text-center group"
                            >
                              <div className="w-8 h-8 mx-auto mb-2 text-gray-500 group-hover:text-pink-400 transition-colors duration-200">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                              </div>
                              <span className="text-xs text-gray-500 group-hover:text-pink-400">Add More</span>
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Pricing Card */}
                <div className="bg-gradient-to-br from-green-900/50 to-emerald-900/50 border border-green-700/50 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-white">Pricing</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-3">
                        Price <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium">€</span>
                        <input
                          type="number"
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                          step="0.01"
                          min="0"
                          required
                          className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-3 text-white bg-gray-800 transition-all duration-200 font-semibold text-lg ${
                            validationErrors.price 
                              ? 'border-red-500 focus:ring-red-500/20 focus:border-red-500' 
                              : 'border-gray-600 focus:ring-green-500/20 focus:border-green-500'
                          }`}
                        />
                      </div>
                      {validationErrors.price && (
                        <p className="mt-2 text-sm text-red-400 flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {validationErrors.price}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-3">
                        Compare Price
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium">€</span>
                        <input
                          type="number"
                          name="comparePrice"
                          value={formData.comparePrice}
                          onChange={handleInputChange}
                          step="0.01"
                          min="0"
                          className="w-full pl-10 pr-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-green-500/20 focus:border-green-500 text-white bg-gray-800 transition-all duration-200"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-3">
                        Cost per Item
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium">€</span>
                        <input
                          type="number"
                          name="costPerItem"
                          value={formData.costPerItem}
                          onChange={handleInputChange}
                          step="0.01"
                          min="0"
                          className="w-full pl-10 pr-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-green-500/20 focus:border-green-500 text-white bg-gray-800 transition-all duration-200"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          name="chargeTax"
                          checked={formData.chargeTax}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className={`w-6 h-6 rounded-lg border-2 transition-all duration-200 ${formData.chargeTax ? 'bg-green-500 border-green-500' : 'border-gray-500 group-hover:border-green-400'}`}>
                          {formData.chargeTax && (
                            <svg className="w-4 h-4 text-white absolute top-0.5 left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-300">Charge tax on this product</span>
                    </label>
                  </div>
                </div>

                {/* Inventory Card */}
                <div className="bg-gradient-to-br from-blue-900/50 to-indigo-900/50 border border-blue-700/50 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-white">Inventory</h3>
                  </div>
                  
                  <div className="space-y-6">
                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          name="trackQuantity"
                          checked={formData.trackQuantity}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className={`w-6 h-6 rounded-lg border-2 transition-all duration-200 ${formData.trackQuantity ? 'bg-blue-500 border-blue-500' : 'border-gray-500 group-hover:border-blue-400'}`}>
                          {formData.trackQuantity && (
                            <svg className="w-4 h-4 text-white absolute top-0.5 left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-gray-300">Track quantity</span>
                    </label>
                    
                    {formData.trackQuantity && (
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-300 mb-3">
                            Quantity <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="number"
                            name="stock"
                            value={formData.stock}
                            onChange={handleInputChange}
                            min="0"
                            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-3 text-white bg-gray-800 transition-all duration-200 ${
                              validationErrors.stock 
                                ? 'border-red-500 focus:ring-red-500/20 focus:border-red-500' 
                                : 'border-gray-600 focus:ring-blue-500/20 focus:border-blue-500'
                            }`}
                          />
                          {validationErrors.stock && (
                            <p className="mt-2 text-sm text-red-400 flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {validationErrors.stock}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          name="continueSellingOutOfStock"
                          checked={formData.continueSellingOutOfStock}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className={`w-6 h-6 rounded-lg border-2 transition-all duration-200 ${formData.continueSellingOutOfStock ? 'bg-blue-500 border-blue-500' : 'border-gray-500 group-hover:border-blue-400'}`}>
                          {formData.continueSellingOutOfStock && (
                            <svg className="w-4 h-4 text-white absolute top-0.5 left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-gray-300">Continue selling when out of stock</span>
                    </label>
                  </div>
                </div>

                {/* Shipping Card */}
                <div className="bg-gradient-to-br from-orange-900/50 to-amber-900/50 border border-orange-700/50 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-orange-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-white">Shipping</h3>
                  </div>
                  
                  <div className="space-y-6">
                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          name="isPhysicalProduct"
                          checked={formData.isPhysicalProduct}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className={`w-6 h-6 rounded-lg border-2 transition-all duration-200 ${formData.isPhysicalProduct ? 'bg-orange-500 border-orange-500' : 'border-gray-500 group-hover:border-orange-400'}`}>
                          {formData.isPhysicalProduct && (
                            <svg className="w-4 h-4 text-white absolute top-0.5 left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-gray-300">This is a physical product</span>
                    </label>
                    
                    {formData.isPhysicalProduct && (
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-300 mb-3">
                            Weight
                          </label>
                          <input
                            type="number"
                            name="weight"
                            value={formData.weight}
                            onChange={handleInputChange}
                            step="0.1"
                            min="0"
                            className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-orange-500/20 focus:border-orange-500 text-white bg-gray-800 transition-all duration-200"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-300 mb-3">
                            Unit
                          </label>
                          <select
                            name="weightUnit"
                            value={formData.weightUnit}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-orange-500/20 focus:border-orange-500 text-white bg-gray-800 transition-all duration-200"
                          >
                            <option value="kg" className="text-white bg-gray-800">kg</option>
                            <option value="g" className="text-white bg-gray-800">g</option>
                            <option value="lb" className="text-white bg-gray-800">lb</option>
                            <option value="oz" className="text-white bg-gray-800">oz</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Product Organization */}
              <div className="space-y-8">
                {/* Status Card */}
                <div className="bg-gray-700 border border-gray-600 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-gray-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-white">Status</h3>
                  </div>
                  
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-pink-500/20 focus:border-pink-500 text-white bg-gray-800 transition-all duration-200 font-medium"
                  >
                    <option value="active" className="text-white bg-gray-800">Active</option>
                    <option value="draft" className="text-white bg-gray-800">Draft</option>
                    <option value="archived" className="text-white bg-gray-800">Archived</option>
                  </select>
                </div>

                {/* Product Organization Card */}
                <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 border border-purple-700/50 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white">Product Organization</h3>
                      <p className="text-sm text-gray-400">Categorize and organize your product</p>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-3">
                        Product Type
                      </label>
                      <input
                        type="text"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-purple-500/20 focus:border-purple-500 text-white bg-gray-800 transition-all duration-200"
                        placeholder="e.g., Apparel, Electronics"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-3">
                        Vendor
                      </label>
                      {enableVendorEdit ? (
                        <select
                          name="vendor"
                          value={formData.vendor}
                          onChange={handleInputChange}
                          disabled={loadingVendors}
                          className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-purple-500/20 focus:border-purple-500 text-white bg-gray-800 transition-all duration-200"
                        >
                          <option value="">
                            {loadingVendors ? 'Loading vendors...' : 'Select a vendor'}
                          </option>
                          {vendorOptions.map((vendor, index) => (
                            <option key={index} value={vendor} className="bg-gray-800 text-white">
                              {vendor}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          name="vendor"
                          value={formData.vendor}
                          onChange={handleInputChange}
                          disabled
                          className="w-full px-4 py-3 border border-gray-600 rounded-xl text-gray-400 bg-gray-900 cursor-not-allowed transition-all duration-200 opacity-60"
                          placeholder="Loaded from submission data"
                        />
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-3">
                        Tags
                      </label>
                      <input
                        type="text"
                        name="tags"
                        value={formData.tags}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-3 focus:ring-purple-500/20 focus:border-purple-500 text-white bg-gray-800 transition-all duration-200"
                        placeholder="summer, cotton, casual, premium"
                      />
                      <p className="text-xs text-gray-500 mt-2">Separate tags with commas</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {submitError && (
              <div className="mt-8 bg-red-900/50 border border-red-700/50 text-red-300 px-6 py-4 rounded-2xl flex items-start space-x-3">
                <div className="w-6 h-6 bg-red-600/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold">Error creating product</p>
                  <p className="text-sm">{submitError}</p>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Professional Modal Actions */}
        <div className="border-t border-gray-600 bg-gray-700 px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-400">
              * Required fields
            </div>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-6 py-3 border border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 hover:shadow-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formData.title}
                onClick={handleSubmit}
                className="px-8 py-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-3 hover:shadow-lg hover:scale-105 active:scale-95"
              >
                {isSubmitting && (
                  <svg className="animate-spin w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                )}
                <span>{isSubmitting ? 'Creating Product...' : 'Create Product'}</span>
                {!isSubmitting && (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProductModal; 
